import { InAppReview } from '@capacitor-community/in-app-review';
import Framework7 from 'framework7';
import { Event, recordEvent } from './analytics';
import { browseURL, device, getRouter, logError } from './app';
import { translate as tr } from './i18n';
import { ANDROID_STORE_URL, IOS_STORE_URL } from './media';
import { ROUTE_REPORT_ISSUE } from './routes';
import { getSessionStats, getStartCount, getStoredValue, setStoredValue, StoreValue } from './settings';
import { openAwaitableDialog, showThankYouDialog } from './utils';


interface DonateState {
    lastPromptTimestamp: number,
    hasDonated: boolean,
    disabled: boolean,
}

const DEFAULT_STATE: DonateState = {
    lastPromptTimestamp: 0,
    hasDonated: false,
    disabled: false,
}

/**
 * Manages prompting users to rate the app based on usage thresholds.
 *
 * The `AppRater` class tracks app launches and the time since the last prompt to determine
 * when to ask the user for an in-app review. It ensures users are not prompted too frequently,
 * and respects whether the user has already rated the app.
 *
 */
export class AppRater {
    private static readonly LAUNCH_COUNT_THRESHOLD = 10;
    private static readonly SESSION_COUNT_THRESHOLD = 10;
    private static readonly DAYS_THRESHOLD = 45;

    constructor() {
    }

    private getState(): DonateState {
        return getStoredValue(StoreValue.donate, DEFAULT_STATE);
    }

    private setState(state: DonateState): void {
        setStoredValue(StoreValue.donate, state);
    }

    public async shouldPromptForDonation(): Promise<boolean> {
        const state = this.getState();
        const now = Date.now();
        const daysThresholdInMs = AppRater.DAYS_THRESHOLD * 24 * 60 * 60 * 1000;
        const timeSinceLastPrompt = now - state.lastPromptTimestamp;

        const rc: Record<string, any> = {}; // eslint-disable-line
        rc.timeSinceLastPrompt = timeSinceLastPrompt;
        rc.launchCount = getStartCount();
        rc.sessionCount = getSessionStats().length;
        rc.disabled = state.disabled === true; // User denied donating forever
        rc.state = state;

        rc.meetsLaunchCount = rc.launchCount >= AppRater.LAUNCH_COUNT_THRESHOLD;
        rc.meetsSessionCount = rc.sessionCount >= AppRater.SESSION_COUNT_THRESHOLD;
        rc.meetsTimeThreshold = rc.timeSinceLastPrompt >= daysThresholdInMs;

        rc.shouldPrompt = !rc.disabled && rc.meetsLaunchCount && rc.meetsSessionCount && rc.meetsTimeThreshold;
        console.log(`donate:shouldPrompt: ${JSON.stringify(rc)}`);

        return rc.shouldPrompt;
    }

    public setDisabled(disabled: boolean) {
        const state = this.getState();
        this.setState({ ...state, ...{ disabled: disabled } });
        console.log(`donate: disabled=${disabled}`);
    }

    public isDisabled(): boolean {
        return this.getState().disabled === true;
    }

    /**
     * Checks whether the user should be prompted for a donation and, if so, updates the last prompt timestamp
     * and triggers the review prompt
     *
     * @returns {Promise<void>} A promise that resolves when the review check and prompt process is complete.
     */
    public async checkAndPromptForDonation(f7: Framework7): Promise<void> {
        if (await this.shouldPromptForDonation()) {
            console.debug('donate:check: prompt=true asking user to donate');
            // update last prompt time now before showing prompt
            // to avoid multiple prompts if user ignores the prompt
            // and to ensure we don't prompt again immediately if the prompt fails
            const state = this.getState();
            state.lastPromptTimestamp = Date.now();
            this.setState(state);
            // request if app liked and review
            await this.showDonateDialog(f7);
        } else {
            console.debug('donate:check: prompt=false not asking user to donate');
        }
    }


    /**
     * Immediately request an in-app review from the user using the InAppReview API..
     */
    public async showDonateDialog(f7: Framework7): Promise<void> {
        try {
            const module = await import('@components/donate-sheet.svelte');
            const DonateSheet = module.default;

            const containerDiv = document.createElement('div');
            const svelteInstance = new DonateSheet({
                target: containerDiv,
                props: {
                    opened: false, // Tells F7-Svelte to slide it up instantly on mount
                }
            });

            const sheetEl: HTMLElement | null = containerDiv.querySelector('.sheet-modal');
            if (!sheetEl) {
                console.error("Could not find donate .sheet-modal element", sheetEl);
                svelteInstance.$destroy();
                containerDiv.remove();
                return;
            }
            document.body.appendChild(sheetEl);
            containerDiv.remove()

            const f7SheetInstance = f7.sheet.open(sheetEl);
            f7SheetInstance.once('closed', () => {
                f7SheetInstance.destroy();
                svelteInstance.$destroy();
                sheetEl.remove();
            });

        } catch (error) {
            logError("Failed display donation dialog", error);
        }
    }

    /**
     * Immediately request an in-app review from the user using the InAppReview API.
     */
    private async requestReview(): Promise<void> {
        if (!device.native) {
            logError('donate: cannot request store review, app is not native');
            return;
        }

        try {
            await InAppReview.requestReview();
            // analytics
            recordEvent(Event.Share, {
                content_type: "review",
                method: "market"
            });
        } catch (err) {
            logError('donate: Failed to request in-app review', err as Error);
            // backup plan: open the store page
            if (device.android) {
                await browseURL(ANDROID_STORE_URL);
            } else if (device.ios) {
                await browseURL(IOS_STORE_URL);
            }
        }
    }

    /**
     * Ask the user if he likes the app, if yes ask to review, if not redirect to feedback
     */
    public async showReviewAppDialog(f7: Framework7): Promise<void> {
        if (!device.native) return; // can only review in native app

        const dialog = f7.dialog.create({
            title: tr("common.rate_app"),
            text: tr("settings.rate_app_question"),
            buttons: [
                { text: tr("common.cancel") },
                { text: tr("common.yes"), strong: true },
                { text: tr("common.no") }
            ],
            onClick: (_, index) => {
                if (index == 1) {
                    // analytics
                    recordEvent(Event.Share, {
                        content_type: "happy",
                        method: "market"
                    });
                    // user happy, redirect to store for rating
                    if (device.android || device.ios) {
                        this.requestReview().then(() => showThankYouDialog(f7));
                    } else {
                        showThankYouDialog(f7); // thanks!
                    }
                } else if (index == 2) {
                    // user not happy, send an email instead of rating on the stores
                    recordEvent(Event.Share, {
                        content_type: "nothappy",
                        method: "market"
                    });
                    // ask user to send feedback if not happy
                    getRouter()?.navigate(ROUTE_REPORT_ISSUE);
                }
            }
        });
        // wait for 'close' event to tmake it async
        return openAwaitableDialog(dialog);
    };

}